import type { QueryObserverResult } from "@tanstack/react-query";
import { requestUnavailable } from "@softmax/observatory-client/transport";
import type { StorePageSnapshot } from "../store-page.queries";
import { emptyStorePageContent } from "../store-page/content-limits";
import { lintStorePageContent } from "../store-page/lint";
import { validateStorePageContent } from "../store-page/validate-content";
import { draftIsDirty } from "./draft-state";
import type { DraftState } from "./draft-model";

export function presentDraft({
  state,
  query,
  enabled,
}: {
  readonly state: DraftState;
  readonly query: QueryObserverResult<StorePageSnapshot, Error>;
  readonly enabled: boolean;
}) {
  const { error, isPending: pending } = query;
  const unavailable = requestUnavailable(error);
  const remote = unavailable ? undefined : query.data;
  const current = remote?.draft;
  const savedContent = { ...emptyStorePageContent, ...current?.content };
  const content = unavailable
    ? emptyStorePageContent
    : (state.draft?.content ?? savedContent);
  const baseRevisionId = state.draft
    ? state.draft.baseRevisionId
    : (current?.current_revision_id ?? null);
  return {
    initialDraft: { content: savedContent, savedContent, baseRevisionId },
    content,
    baseRevisionId,
    currentRevisionId:
      state.conflict?.current_revision_id ??
      current?.current_revision_id ??
      null,
    status: current?.status ?? null,
    firstPublishedAt: current?.first_published_at ?? null,
    metadataReady:
      current === null ||
      (current?.status !== undefined &&
        current.first_published_at !== undefined),
    loaded: remote !== undefined,
    loading: enabled && pending,
    readFailure: error
      ? {
          operation: remote ? ("refresh" as const) : ("load" as const),
          message: remote ? `Refresh failed: ${error.message}` : error.message,
        }
      : null,
    revisions: remote?.revisions ?? [],
    conflict: state.conflict,
    lastAction: state.lastAction,
    dirty: draftIsDirty(state.draft),
    lintWarnings: lintStorePageContent(content),
    fieldErrors: validateStorePageContent(content),
  };
}
